<script lang="ts">
  import { browser } from '$app/environment';
  import { page } from '$app/stores';
  import { onMount } from 'svelte';
  import Navigation from '$lib/components/layout/Navigation.svelte';
  import Footer from '$lib/components/layout/Footer.svelte';
  import '../app.css';

  // Global scroll animations
  onMount(() => {
    if (browser) {
      // Global IntersectionObserver for [data-animate] elements
      // Mirrors the original site's scroll animation behavior
      const animObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
            }
          });
        },
        { threshold: 0.15 }
      );
      
      // Observe existing elements
      document.querySelectorAll('[data-animate]').forEach(el => animObserver.observe(el));
      
      // Also observe dynamically added elements via MutationObserver
      const mutObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              if (node.hasAttribute('data-animate')) {
                animObserver.observe(node);
              }
              node.querySelectorAll?.('[data-animate]')?.forEach((el: Element) => animObserver.observe(el));
            }
          });
        });
      });
      mutObserver.observe(document.body, { childList: true, subtree: true });
      
      return () => {
        animObserver.disconnect();
        mutObserver.disconnect();
      };
    }
  });
</script>

<svelte:head>
  <link rel="canonical" href={`https://testmachine.ai${$page.url.pathname === '/' ? '' : $page.url.pathname.replace(/\/$/, '')}`} />
  <!-- Title, description and Open Graph tags are set per page. Defining them here
       renders them first in <head>, and link unfurlers use the first og:title. -->
</svelte:head>

<Navigation />

<main>
  <slot />
</main>

<Footer />

<style>
  :global(html) {
    scroll-behavior: smooth;
    scroll-padding-top: var(--nav-h);
    -webkit-text-size-adjust: 100%;
  }

  :global(body) {
    font-family: var(--font);
    font-size: 17px;
    line-height: 1.7;
    color: var(--fg);
    background: var(--bg);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
    transition: background var(--transition), color var(--transition);
    margin: 0;
    padding: 0;
  }

  main {
    min-height: 100vh;
  }
</style>